import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'

import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import { toast } from '@/modules/base/components/toast/toastConfig'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import FileService from '@/modules/file/fileService'
import { ButtonSquare } from '@/modules/base/components/buttons/index'
import { closeFixErrorsDrawer } from '@/redux/ducks/documentExport/list'
import FixErrorsIntro from './fix-errors-intro'
import FixErrorsDocumentStep from './fix-errors-document-step'
import { FailedDocument, FileInfoEntry } from './types'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'

const fixErrorsLang = lang.documentExport.fixErrors

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 85px)',
  },
  content: {
    flex: '1 1 auto',
    overflow: 'auto',
    padding: 30,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    flexShrink: 0,
    padding: '20px 30px',
    borderTop: theme.border,
  },
}))

const FixErrorsDrawer: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { fixErrorsOpen, fixErrorsDocuments } = useSelector((state: any) => state.documentExport.list)

  const documents: FailedDocument[] = fixErrorsDocuments ?? []
  const [step, setStep] = useState(0)
  const [fileInfos, setFileInfos] = useState<Record<string, FileInfoEntry>>({})

  const currentItem = step > 0 ? documents[step - 1] : null
  const isLastStep = step === documents.length

  const updateFileInfo = (docId: string, update: FileInfoEntry) => {
    setFileInfos(prev => ({ ...prev, [docId]: { ...prev[docId], ...update } }))
  }

  useEffect(() => {
    if (!currentItem || fileInfos[currentItem.docId]) {
      return
    }

    const { docId } = currentItem
    updateFileInfo(docId, { status: 'loading' })
    FileService.fetchFileInfo(docId)
      .then(({ body }) => updateFileInfo(docId, { status: 'loaded', file: body }))
      .catch((e) => updateFileInfo(docId, { status: e?.statusCode === 404 || e?.body?.meta?.id?.[0]?.code === 'NOT_FOUND' ? 'notFound' : 'error' }))
  }, [step])

  const onClose = () => {
    if (Object.values(fileInfos).some(fi => !!fi?.resolution)) {
      toast.showToast({
        message: fixErrorsLang.withResolutionMessage,
        type: 'success',
      })
    } else {
      toast.showToast({
        message: fixErrorsLang.withoutResolutionMessage,
        type: 'info',
      })
    }
    dispatch(closeFixErrorsDrawer())
  }

  const headline = currentItem
    ? fixErrorsLang.stepOfLabel(step, documents.length)
    : fixErrorsLang.drawerTitle

  return (
    <SimpleDrawer
      open={Boolean(fixErrorsOpen)}
      onClose={onClose}
      headline={headline}
      width="1100px"
      dataQsId="datev-fix-errors-drawer"
    >
      <div className={classes.container}>
        <div className={classes.content}>
          {currentItem
            ? (
              <FixErrorsDocumentStep
                item={currentItem}
                fileInfo={fileInfos[currentItem.docId]}
                onFileChange={(update) => updateFileInfo(currentItem.docId, update)}
              />
            )
            : <FixErrorsIntro documents={documents} />}
        </div>
        <div className={classes.footer}>
          <ButtonIconSquare
            width='100%'
            label={fixErrorsLang.back}
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep(step - 1)}
            dataQsId="datev-fix-errors-back"
          />
          <ButtonSquare
            label={isLastStep ? fixErrorsLang.done : fixErrorsLang.next}
            variant="small"
            onClick={() => isLastStep ? onClose() : setStep(step + 1)}
            dataQsId="datev-fix-errors-next"
          />
        </div>
      </div>
    </SimpleDrawer>
  )
}

export default FixErrorsDrawer
